import React, { useState, useEffect, useRef } from "react";
import "../index.scss";
import { FaSoundcloud } from "react-icons/fa";
import { FaApple } from "react-icons/fa6";
import { Triangle } from "react-loader-spinner";
import playlistsData from "../data/playlists.json";

const LyricsSpinner = Triangle;
const { defaultPlaylist: DEFAULT_PLAYLIST, playlists: TOP_PLAYLISTS } =
  playlistsData;

const formatTime = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const getProgressPercent = (progressMs, durationMs) => {
  if (!durationMs || durationMs <= 0) return 0;
  return Math.min(100, (progressMs / durationMs) * 100);
};

// Parse LRC synced lyrics into [{ timeMs, text }]
const parseLrc = (syncedLyrics) => {
  if (!syncedLyrics || typeof syncedLyrics !== "string") return [];
  const lines = syncedLyrics.trim().split("\n");
  const result = [];
  const timeRegex = /\[(\d+):(\d{2})\.(\d{2,3})\]\s*(.*)/;
  for (const line of lines) {
    const m = line.match(timeRegex);
    if (m) {
      const mins = parseInt(m[1], 10);
      const secs = parseInt(m[2], 10);
      const frac = m[3];
      const ms =
        frac.length === 3 ? parseInt(frac, 10) : parseInt(frac, 10) * 10;
      const timeMs = (mins * 60 + secs) * 1000 + ms;
      const text = (m[4] || "").trim();
      result.push({ timeMs, text });
    }
  }
  return result.sort((a, b) => a.timeMs - b.timeMs);
};

const getCurrentLineIndex = (lines, progressMs) => {
  let idx = 0;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].timeMs <= progressMs) idx = i;
    else break;
  }
  return idx;
};

export default function SpotifyPlayer({
  isModalOpen,
  selectedPlaylist: propSelectedPlaylist,
  onOpenModal,
  onCloseModal,
}) {
  const [currentTrack, setCurrentTrack] = useState(DEFAULT_PLAYLIST);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [modalTheme, setModalTheme] = useState(() => {
    const saved = localStorage.getItem("ModalTheme");
    return saved || "light";
  });

  useEffect(() => {
    const handleThemeChange = () => {
      setModalTheme(localStorage.getItem("ModalTheme") || "light");
    };
    window.addEventListener("modalThemeChange", handleThemeChange);
    return () =>
      window.removeEventListener("modalThemeChange", handleThemeChange);
  }, []);
  const [displayProgressMs, setDisplayProgressMs] = useState(0);
  const progressBaseRef = useRef({ baseMs: 0, receivedAt: 0, durationMs: 0 });
  const hasRefetchedAtEndRef = useRef(false);
  const [showLyrics, setShowLyrics] = useState(false);
  const [lyricsLines, setLyricsLines] = useState([]);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const prevLineIndexRef = useRef(-1);
  const [transitioningLine, setTransitioningLine] = useState({
    prev: null,
    next: null,
  });

  const isPlaylist =
    currentTrack === DEFAULT_PLAYLIST ||
    (currentTrack.name === DEFAULT_PLAYLIST.name &&
      currentTrack.artist === DEFAULT_PLAYLIST.artist);

  const fetchCurrentTrack = React.useCallback(async () => {
    try {
      const response = await fetch("/api/spotify/currently-playing");
      const data = response.ok ? await response.json() : null;
      if (data && data.name) {
        setCurrentTrack(data);
      } else {
        setCurrentTrack(DEFAULT_PLAYLIST);
      }
    } catch (error) {
      console.error("Error fetching Spotify track:", error);
      setCurrentTrack(DEFAULT_PLAYLIST);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchCurrentTrack();
  };

  const toggleModalTheme = () => {
    const newTheme = modalTheme === "light" ? "dark" : "light";
    setModalTheme(newTheme);
    localStorage.setItem("ModalTheme", newTheme);
    window.dispatchEvent(new Event("modalThemeChange"));
  };

  useEffect(() => {
    fetchCurrentTrack();
    const interval = setInterval(fetchCurrentTrack, 15000); // Poll every 15 seconds
    return () => clearInterval(interval);
  }, [fetchCurrentTrack]);

  // Update progress base when track changes
  useEffect(() => {
    if (isPlaylist) return;
    hasRefetchedAtEndRef.current = false;
    const durationMs = currentTrack.durationMs ?? 0;
    const baseMs = currentTrack.progressMs ?? 0;
    progressBaseRef.current = {
      baseMs,
      receivedAt: Date.now(),
      durationMs,
    };
    setDisplayProgressMs(baseMs);
  }, [currentTrack, isPlaylist]);

  // Auto-progress bar and refetch when song ends
  useEffect(() => {
    if (isPlaylist) return;
    const durationMs = progressBaseRef.current.durationMs;
    if (!durationMs || durationMs <= 0) return;

    const interval = setInterval(() => {
      const { baseMs, receivedAt } = progressBaseRef.current;
      const elapsed = Date.now() - receivedAt;
      const progress = Math.min(baseMs + elapsed, durationMs);
      setDisplayProgressMs(progress);

      // Refetch when end reached (catches next track, or real position if user scrubbed back)
      if (progress >= durationMs && !hasRefetchedAtEndRef.current) {
        hasRefetchedAtEndRef.current = true;
        fetchCurrentTrack();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaylist, currentTrack?.name, fetchCurrentTrack]);

  // Fetch lyrics when track changes and lyrics mode is on
  useEffect(() => {
    if (isPlaylist || !showLyrics) return;
    const track = currentTrack.name;
    const artist = currentTrack.artist;
    if (!track || !artist) return;
    setLyricsLoading(true);
    setLyricsLines([]);
    prevLineIndexRef.current = -1;
    setTransitioningLine({ prev: null, next: null });
    fetch(
      `/api/lyrics?track=${encodeURIComponent(track)}&artist=${encodeURIComponent(artist)}`,
    )
      .then((r) => (r.ok ? r.json() : { syncedLyrics: null }))
      .then((data) => {
        const parsed = parseLrc(data?.syncedLyrics);
        setLyricsLines(parsed);
      })
      .catch(() => setLyricsLines([]))
      .finally(() => setLyricsLoading(false));
  }, [currentTrack?.name, currentTrack?.artist, showLyrics, isPlaylist]);

  // Update line transition when progress changes (lyrics mode)
  const currentLineIndex = lyricsLines.length
    ? getCurrentLineIndex(lyricsLines, displayProgressMs)
    : -1;
  const currentLineText =
    currentLineIndex >= 0 && lyricsLines[currentLineIndex]
      ? lyricsLines[currentLineIndex].text
      : null;

  useEffect(() => {
    if (!showLyrics || lyricsLines.length === 0) return;
    if (currentLineIndex !== prevLineIndexRef.current) {
      const prevIdx = prevLineIndexRef.current;
      const prevText =
        prevIdx >= 0 && lyricsLines[prevIdx] ? lyricsLines[prevIdx].text : null;
      setTransitioningLine({ prev: prevText, next: currentLineText });
      prevLineIndexRef.current = currentLineIndex;
      const t = setTimeout(
        () => setTransitioningLine({ prev: null, next: null }),
        350,
      );
      return () => clearTimeout(t);
    }
  }, [currentLineIndex, currentLineText, lyricsLines, showLyrics]);

  // Sync selectedPlaylist with prop
  useEffect(() => {
    if (propSelectedPlaylist) {
      setSelectedPlaylist(propSelectedPlaylist);
    }
  }, [propSelectedPlaylist]);

  // Reset loading state when modal opens or playlist changes
  useEffect(() => {
    if (isModalOpen) {
      setIsIframeLoading(true);
    }
  }, [isModalOpen, selectedPlaylist]);

  return (
    <>
      <div className="spotify-player-container">
        <div
          className={`spotify-player ${!isPlaylist ? "playing-track" : ""}`}
          onMouseEnter={fetchCurrentTrack}
        >
          <div className="spotify-corner spotify-corner-top-left"></div>
          <div className="spotify-corner spotify-corner-top-right"></div>
          <div className="spotify-corner spotify-corner-bottom-left"></div>
          <div className="spotify-corner spotify-corner-bottom-right"></div>

          <div className="spotify-icon-wrapper">
            <i className="fa-brands fa-spotify"></i>
          </div>

          <div className="spotify-expanded-content">
            <div
              className={`spotify-now-listening ${isPlaylist ? "spotify-now-listening--empty" : ""}`}
            >
              <div className="spotify-now-listening-header">
                <span className="spotify-now-listening-label">
                  Listening to Spotify
                </span>
                <i className="fa-brands fa-spotify spotify-now-listening-logo"></i>
                <button
                  className={`spotify-now-listening-mic ${showLyrics ? "is-active" : ""}`}
                  onClick={() => setShowLyrics((v) => !v)}
                  aria-label={showLyrics ? "Show progress" : "Show lyrics"}
                  title={showLyrics ? "Show progress" : "Show lyrics"}
                >
                  <i className="fa-solid fa-microphone"></i>
                </button>
                <button
                  className="spotify-now-listening-refresh"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  aria-label="Refresh"
                  title="Refresh"
                >
                  <i
                    className={`fa-solid fa-rotate ${isRefreshing ? "fa-spin" : ""}`}
                  ></i>
                </button>
              </div>
              {!isPlaylist && (
                <>
                  {!showLyrics ? (
                    <div className="spotify-now-listening-track">
                      <img
                        src={currentTrack.albumArt}
                        alt={currentTrack.name}
                        className="spotify-now-listening-art"
                      />
                      <div className="spotify-now-listening-details">
                        {currentTrack.songUrl ? (
                          <a
                            href={currentTrack.songUrl}
                            className="spotify-now-listening-title"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {currentTrack.name}
                          </a>
                        ) : (
                          <div className="spotify-now-listening-title">
                            {currentTrack.name}
                          </div>
                        )}
                        <div className="spotify-now-listening-artist">
                          {currentTrack.artist}
                        </div>
                        <div className="spotify-now-listening-progress">
                          <span className="spotify-now-listening-time">
                            {formatTime(displayProgressMs)}
                          </span>
                          <div className="spotify-now-listening-bar">
                            <div
                              className="spotify-now-listening-bar-fill"
                              style={{
                                width: `${getProgressPercent(displayProgressMs, currentTrack.durationMs ?? 0)}%`,
                              }}
                            />
                          </div>
                          <span className="spotify-now-listening-time">
                            {formatTime(currentTrack.durationMs ?? 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="spotify-now-listening-lyrics">
                      {lyricsLoading ? (
                        <div className="spotify-lyrics-loading">
                          <LyricsSpinner
                            visible={true}
                            height={18}
                            width={18}
                            color="currentColor"
                            ariaLabel="loading"
                          />
                        </div>
                      ) : lyricsLines.length === 0 ? (
                        <div className="spotify-lyrics-empty">
                          No lyrics found
                        </div>
                      ) : (
                        <div
                          className="spotify-lyrics-track"
                          key={currentLineIndex}
                        >
                          {transitioningLine.prev != null && (
                            <div className="spotify-lyrics-line spotify-lyrics-line--exit">
                              {transitioningLine.prev}
                            </div>
                          )}
                          <div className="spotify-lyrics-line spotify-lyrics-line--current">
                            {transitioningLine.next != null
                              ? transitioningLine.next
                              : currentLineText || "—"}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="spotify-playlists-section">
              <div className="spotify-section-title">top playlists </div>
              <div className="spotify-playlists-list">
                {TOP_PLAYLISTS.map((playlist, index) => (
                  <div
                    key={index}
                    className="spotify-playlist-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        playlist.platform === "soundcloud" ||
                        playlist.platform === "apple"
                      ) {
                        window.open(
                          playlist.url,
                          "_blank",
                          "noopener,noreferrer",
                        );
                        return;
                      }
                      setIsIframeLoading(true);
                      onOpenModal(playlist);
                    }}
                  >
                    <div className="spotify-playlist-image-wrapper">
                      <img
                        src={`/img/playlists/${playlist.image}.${playlist.extension || "png"}`}
                        alt={playlist.name}
                        className="spotify-playlist-image"
                      />
                      {playlist.platform && (
                        <div
                          className={`spotify-playlist-image-icon ${playlist.platform}`}
                        >
                          {playlist.platform === "soundcloud" && (
                            <FaSoundcloud />
                          )}
                          {playlist.platform === "apple" && <FaApple />}
                        </div>
                      )}
                    </div>
                    <span
                      className={`spotify-playlist-name ${playlist.platform ? "isPlatform" : ""}`}
                    >
                      {playlist.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="project-modal-overlay" onClick={onCloseModal}>
          <div
            className="project-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: modalTheme === "light" ? "#ffffff" : "#000c",
              border:
                modalTheme === "light" ? "1px solid #0002" : "1px solid #fff2",
            }}
          >
            <div
              className="project-modal-header"
              style={{
                background: modalTheme === "light" ? "#f5f5f5" : "#000c",
                borderBottom: "none",
              }}
            >
              <div
                className="project-modal-title"
                style={{ color: modalTheme === "light" ? "#000" : "#fff" }}
              >
                {
                  (selectedPlaylist || propSelectedPlaylist || DEFAULT_PLAYLIST)
                    .name
                }
              </div>
              <div className="project-modal-actions">
                <button
                  className="project-modal-btn refresh-btn"
                  onClick={toggleModalTheme}
                  title={
                    modalTheme === "light"
                      ? "Switch to Dark Theme"
                      : "Switch to Light Theme"
                  }
                  style={{
                    borderColor: modalTheme === "light" ? "#ccc" : "#fff4",
                    color: modalTheme === "light" ? "#000" : "#fff",
                  }}
                >
                  <i
                    className={`fa fa-${modalTheme === "light" ? "moon" : "sun"}`}
                  />
                </button>
                <button
                  className="project-modal-btn close-btn"
                  onClick={() => {
                    setSelectedPlaylist(null);
                    setIsIframeLoading(true);
                    onCloseModal();
                  }}
                  title="Close"
                  style={{
                    borderColor: modalTheme === "light" ? "#ccc" : "#fff4",
                    color: modalTheme === "light" ? "#000" : "#fff",
                  }}
                >
                  <i className="fa fa-times" />
                </button>
              </div>
            </div>
            <div
              className="project-modal-content"
              style={{
                background: modalTheme === "light" ? "#ffffff" : "#000c",
              }}
            >
              {(() => {
                const playlist =
                  selectedPlaylist || propSelectedPlaylist || DEFAULT_PLAYLIST;
                const playlistUrl = playlist.url || playlist.playlistUrl;
                const isSpotify =
                  !playlist.platform || playlist.platform === "spotify";

                if (!isSpotify) {
                  const platformName =
                    playlist.platform === "apple"
                      ? "Apple Music"
                      : "SoundCloud";
                  const Icon =
                    playlist.platform === "apple" ? FaApple : FaSoundcloud;
                  return (
                    <div className="spotify-modal-external">
                      <a
                        href={playlistUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="spotify-modal-external-link"
                      >
                        <Icon className="spotify-modal-external-icon" />
                        <span>Open in {platformName}</span>
                      </a>
                    </div>
                  );
                }

                const url = new URL(playlistUrl);
                const playlistId = url.pathname
                  .split("/")
                  .filter(Boolean)
                  .pop();
                const themeParam = modalTheme === "dark" ? "0" : "1";
                const embedSrc = `https://open.spotify.com/embed/playlist/${playlistId}?utm_source=generator&theme=${themeParam}`;

                return (
                  <>
                    {isIframeLoading && (
                      <div className="spotify-modal-loading">
                        <i className="fa-brands fa-spotify spotify-loading-icon"></i>
                      </div>
                    )}
                    <iframe
                      data-testid="embed-iframe"
                      title={`${playlist.name} - Spotify Playlist`}
                      src={embedSrc}
                      width="100%"
                      height="352"
                      frameBorder="0"
                      allowFullScreen
                      allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                      loading="lazy"
                      onLoad={() => setIsIframeLoading(false)}
                      style={{
                        borderRadius: "12px",
                        width: "100%",
                        height: "100%",
                        border: "none",
                        position: "relative",
                        zIndex: "2",
                      }}
                    />
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
